import type { ComponentProps } from "react";
import { createContext, useContext, useState } from "react";
import rehypeTruncate from "rehype-truncate";
import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { isCurrentProduct, isPreviousProduct } from "@/domains/careplan/factories";
import { getCareplanProductPrice } from "@/domains/careplan/factories/get-careplan-product-price";
import { useCareplanOrderLine } from "@/domains/careplan/hooks/use-careplan-order";
import { useRestoreCareplanCartLine } from "@/domains/careplan/hooks/use-restore-careplan-cart-line";
import {
  CAREPLAN_CHANGE_ATTRIBUTES,
  UPDATED_INSTRUCTIONS_MARKDOWN_STYLES,
} from "@/domains/careplan/models/constants";
import type {
  CareplanProductDataModel,
  PreviousCareplanProductDataModel,
} from "@/domains/careplan/models/types";
import { getCartLinkState } from "@/domains/careplan/utils/cart-link-state";
import {
  formatRating,
  formatReviewCount,
  getDisplayableRating,
} from "@/domains/careplan/utils/rating";
import { isCartProcessing, useCartStore } from "@/stores";
import {
  Accordion,
  Card,
  Carousel as UICarousel,
  focusRing,
  Heading,
  type HeadingProps,
  icons,
  MarkdownRenderer,
  Modal,
  Reveal,
  Skeleton,
  Stars,
} from "@/ui";

const productCardVariants = tv({
  slots: {
    name: undefined,
    image:
      "h-48 w-full rounded-2xl bg-product-media object-contain sm:aspect-square sm:h-auto sm:w-60 sm:object-cover",
    carousel: "overflow-hidden rounded-2xl",
    description: "text-base text-content-secondary sm:text-xl",
    price: "flex flex-col items-end md:flex-row md:items-baseline md:gap-2",
    priceAmount: "text-xl font-semibold text-content-secondary sm:text-2xl",
    priceSuffix: "text-base",
    listPrice: "text-base text-content-subtle line-through md:text-lg",
    rating: "flex items-center gap-1.5 text-base leading-none text-content md:text-lg",
    format: "font-crato-mono text-base font-semibold md:text-lg",
    cartLinkRoot: "flex shrink-0",
    cartLink: [
      "inline-flex min-h-11 cursor-pointer flex-wrap items-center gap-x-1.5 rounded-sm text-left text-base underline-offset-4 transition-colors",
      "hover:underline disabled:cursor-not-allowed disabled:text-content-subtle",
      focusRing,
    ],
    cartLinkPrefix: "font-normal text-content-secondary",
    cartLinkAction: "inline-flex items-center gap-1 font-semibold whitespace-nowrap text-content",
    cartLinkText: "text-base whitespace-nowrap text-content-secondary",
    updatedAt: "p-1 text-base sm:text-xl",
    whyRecommended: "text-lg font-semibold text-content-secondary sm:text-xl",
  },
  variants: {
    recommended: {
      true: {
        updatedAt: "bg-accent-surface text-content-secondary",
      },
      false: {
        updatedAt: "bg-danger-surface text-content",
      },
    },
  },
});

const {
  carousel,
  cartLink,
  cartLinkAction,
  cartLinkPrefix,
  cartLinkRoot,
  cartLinkText,
  description,
  format,
  image,
  listPrice,
  name,
  price,
  priceAmount,
  priceSuffix,
  rating,
  updatedAt,
  whyRecommended,
} = productCardVariants();

export type ProductCardProps = {
  product: CareplanProductDataModel | PreviousCareplanProductDataModel;
} & ComponentProps<"div">;

type ProductCardContextType = (CareplanProductDataModel | PreviousCareplanProductDataModel) & {
  selectedDetailSlug: string | null;
  setSelectedDetailSlug: (slug: string | null) => void;
};

const ProductCardContext = createContext<ProductCardContextType | null>(null);

export const useProductCardContext = () => {
  const product = useContext(ProductCardContext);
  if (!product) {
    throw Error("useProductCardContext can only be used inside a ProductCardContext.Provider");
  }

  return product;
};

const UpdatedAt = ({ className }: ComponentProps<"p">) => {
  const careplan = useCareplanContext();
  const product = useProductCardContext();
  const previousProduct = isPreviousProduct(product);
  const currentProduct = isCurrentProduct(product);

  if (previousProduct) {
    return <p className={updatedAt({ className, recommended: false })}>No Longer Recommended</p>;
  }

  if (currentProduct) {
    const hasChanges = !!currentProduct.changes?.length;

    const isNewRecommendation = !!currentProduct.changes?.some((change) => {
      return change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.PRODUCT;
    });

    if (!hasChanges) {
      return null;
    }

    return (
      <p className={updatedAt({ className, recommended: true })}>
        {isNewRecommendation ? "New Recommendation" : `Updated ${careplan.updatedAt}`}
      </p>
    );
  }
};

const WhyRecommended = ({ className }: ComponentProps<"p">) => {
  const product = useProductCardContext();

  if (!product.whyRecommended) {
    return null;
  }

  return <p className={whyRecommended({ className })}>{product.whyRecommended.title}</p>;
};

const Name = ({ className, ...props }: HeadingProps) => {
  const product = useProductCardContext();

  return (
    <Heading className={name({ className })} {...props}>
      {product.name}
    </Heading>
  );
};

const Price = ({ className, ...props }: ComponentProps<"div">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);
  const { isLoading, line } = useCareplanOrderLine(
    currentProduct ? currentProduct.variantId : null,
  );

  const productPrice = getCareplanProductPrice({
    isLoading: !!currentProduct && isLoading,
    line,
    listPriceAsString: product.priceAsString,
  });

  if (productPrice.status === "loading") {
    return (
      <div className={price({ className })} {...props}>
        <Skeleton className="h-6 w-14 sm:h-7 sm:w-16" />
        <Skeleton className="mt-1 h-4 w-9 md:mt-0" />
        <span className="sr-only">Loading price</span>
      </div>
    );
  }

  if (productPrice.status === "list") {
    return (
      <div className={price({ className })} {...props}>
        <p className={priceAmount()}>${productPrice.amount}</p>
      </div>
    );
  }

  return (
    <div className={price({ className })} {...props}>
      <p className={priceAmount()}>
        ${productPrice.amount}
        <span className={priceSuffix()}>/mo</span>
      </p>
      {productPrice.listAmount ? (
        <p className={listPrice()}>
          <span className="sr-only">Retail price </span>${productPrice.listAmount}
        </p>
      ) : null}
    </div>
  );
};

type CartLinkProps = ComponentProps<"div"> & {
  compact?: boolean;
};

const CartLink = ({ className, compact = false }: CartLinkProps) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);
  const { isLoading, line, order } = useCareplanOrderLine(
    currentProduct ? currentProduct.variantId : null,
  );
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const isProcessing = useCartStore(isCartProcessing);
  const { canRestore, isPending, restore } = useRestoreCareplanCartLine();

  if (!currentProduct) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={cartLinkRoot({ className })}>
        <Skeleton className="h-5 w-36" />
        <span className="sr-only">Loading cart status</span>
      </div>
    );
  }

  if (!line || !order) {
    return null;
  }

  const state = getCartLinkState({
    line,
    subscriptionStatus: order.subscriptionStatus,
    isProcessing,
  });
  const copy = compact ? state.compact : state;

  if (state.intent === "none") {
    return (
      <div className={cartLinkRoot({ className })}>
        <p className={cartLinkText()}>{copy.prefix}</p>
      </div>
    );
  }

  const isRestore = state.intent === "restore";
  const isRestoreDisabled = isRestore && (isPending || !canRestore);

  const handleClick = () => {
    if (isRestore) {
      analytics.track("careplan_product_add_to_cart_clicked", { sku: currentProduct.sku });
      restore(order.cartId, line);

      return;
    }

    analytics.track("careplan_product_view_cart_clicked", { sku: currentProduct.sku });
    openCart();
  };

  return (
    <div className={cartLinkRoot({ className })}>
      <button
        className={cartLink()}
        disabled={isRestoreDisabled}
        onClick={handleClick}
        type="button"
      >
        {copy.prefix ? <span className={cartLinkPrefix()}>{copy.prefix}</span> : null}
        <span className={cartLinkAction()}>
          {copy.action}
          {isRestore ? (
            <icons.PlusIcon className="size-4" aria-hidden />
          ) : (
            <icons.ArrowRight className="size-4" aria-hidden />
          )}
        </span>
      </button>
    </div>
  );
};

const Format = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  return (
    <p className={format({ className })} {...props}>
      {currentProduct.format}
    </p>
  );
};

const Rating = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  const displayable = getDisplayableRating(currentProduct.rating, currentProduct.reviewCount);

  if (!displayable) {
    return null;
  }

  const { rating: score, reviewCount } = displayable;

  return (
    <p className={rating({ className })} {...props}>
      <Stars className="md:h-4.5" rating={score} />
      <span className="font-bold" aria-hidden>
        {formatRating(score)}
      </span>
      <span className="text-content-secondary">{`(${formatReviewCount(reviewCount)})`}</span>
    </p>
  );
};

const Carousel = ({ className }: ComponentProps<"div">) => {
  const product = useProductCardContext();

  return (
    <UICarousel.Root
      className={carousel({ className })}
      renderSlide={({ id, url }) => {
        return (
          <div
            className="flex w-full flex-none items-center justify-center rounded-2xl select-none last:pr-0"
            key={id}
          >
            <img
              alt={product.name}
              className="h-80 w-full rounded-xl bg-product-media object-contain"
              src={url}
            />
          </div>
        );
      }}
      slides={product.images.map((url, index) => {
        return {
          url,
          id: index,
        };
      })}
    >
      <UICarousel.Slider className="overflow-visible">
        <UICarousel.Slides />
      </UICarousel.Slider>
      <UICarousel.ArrowNavigation>
        <UICarousel.PrevButton />
        <UICarousel.NextButton />
      </UICarousel.ArrowNavigation>
    </UICarousel.Root>
  );
};

const Description = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();

  return (
    <p className={description({ className })} {...props}>
      {product.description}
    </p>
  );
};

const Instructions = () => {
  const WHY_THIS_ACCORDION_KEY = "why-this";
  const HOW_IT_WORKS_MODAL_SLUG = "how-it-works";

  const { setSelectedDetailSlug, ...product } = useProductCardContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  const instructionsChanged = !!currentProduct.changes?.some((change) => {
    return (
      change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.DOSAGE ||
      change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.FREQUENCY
    );
  });

  const howItWorksDetail = currentProduct.details.find((detail) => {
    return detail.slug === HOW_IT_WORKS_MODAL_SLUG;
  });

  return (
    <div className="rounded-xl border border-line">
      {currentProduct.whyRecommended?.content ? (
        <Accordion.Root
          onValueChange={(value) => {
            if (value === WHY_THIS_ACCORDION_KEY) {
              analytics.track("product_why_recommended_viewed", {
                sku: currentProduct.sku,
              });
            }

            return setIsExpanded(value === WHY_THIS_ACCORDION_KEY);
          }}
          type="single"
          collapsible
        >
          <Accordion.Item className="rounded-t-xl bg-surface-sunken" value={WHY_THIS_ACCORDION_KEY}>
            <Accordion.Header className="flex flex-col px-4">
              <Accordion.Trigger className="text-base font-bold text-content-secondary sm:text-xl">
                Why this?
                <Accordion.Icon />
              </Accordion.Trigger>
              <Reveal>
                <div className="pb-4 text-base text-content-secondary sm:text-xl">
                  <MarkdownRenderer
                    rehypePlugins={[[rehypeTruncate, { maxChars: 200, disable: isExpanded }]]}
                  >
                    {currentProduct.whyRecommended?.content}
                  </MarkdownRenderer>
                </div>
              </Reveal>
            </Accordion.Header>

            <Accordion.Content className="flex flex-col gap-4 px-4">
              {howItWorksDetail?.body ? (
                <button
                  className={`w-fit cursor-pointer rounded-md py-1 font-semibold underline underline-offset-4 transition-colors hover:text-accent ${focusRing}`}
                  onClick={() => {
                    return setSelectedDetailSlug(HOW_IT_WORKS_MODAL_SLUG);
                  }}
                  type="button"
                >
                  See How It Works For Your Symptoms
                </button>
              ) : null}
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      ) : null}
      <div className="p-4 text-base text-content-secondary sm:text-xl">
        <MarkdownRenderer
          components={instructionsChanged ? UPDATED_INSTRUCTIONS_MARKDOWN_STYLES : undefined}
        >
          {currentProduct.usageInstructions}
        </MarkdownRenderer>
      </div>
    </div>
  );
};

const DosingAndEffects = () => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line text-center text-base text-content-secondary sm:text-lg">
      <table className="w-full border-collapse">
        <tbody>
          <tr className="divide-x divide-line border-line">
            <td className="p-4">
              <div className="flex items-center justify-center gap-4">
                <icons.Onset className="hidden size-11 sm:block" />

                <div className="flex flex-col items-start">
                  <div>Effect Onset:</div>

                  <div className="font-bold">{currentProduct.effectOnset}</div>
                </div>
              </div>
            </td>

            <td className="p-4">
              <div className="flex items-center justify-center gap-4">
                <icons.Duration className="hidden size-11 sm:block" />
                <div className="flex flex-col items-start">
                  <div>Effect Duration:</div>

                  <div className="font-bold">{currentProduct.effectDuration}</div>
                </div>
              </div>
            </td>
          </tr>

          <tr className="border-t border-line">
            <td className="p-4" colSpan={2}>
              <p className="leading-6 text-content-secondary sm:text-xl">
                {currentProduct.productSupply}
              </p>
            </td>
          </tr>

          <tr className="border-t border-line">
            <td className="p-4" colSpan={2}>
              <p className="leading-6 text-content-secondary sm:text-xl">
                Contains: {currentProduct.allIngredients}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const Image = ({ className }: ComponentProps<"img">) => {
  const product = useProductCardContext();

  return <img alt={product.name} className={image({ className })} src={product.images[0]} />;
};

const Details = () => {
  const { selectedDetailSlug, setSelectedDetailSlug, ...product } = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  const selectedDetail = currentProduct.details.find((detail) => {
    return detail.slug === selectedDetailSlug;
  });

  return (
    <>
      {!!selectedDetail && (
        <Modal
          body={<MarkdownRenderer resetConfig>{selectedDetail.body}</MarkdownRenderer>}
          className="max-h-[80vh] overflow-scroll"
          closeModal={() => {
            setSelectedDetailSlug(null);
          }}
          isOpen={!!selectedDetail}
          title={selectedDetail.title}
        />
      )}
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        {currentProduct.details.map((detail) => {
          if (!detail.body) {
            return null;
          }

          return (
            <button
              className={`flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-md bg-surface-sunken px-3 py-2 text-left font-semibold text-content-secondary transition-colors hover:bg-surface-hover hover:text-accent ${focusRing}`}
              key={detail.id}
              onClick={() => {
                setSelectedDetailSlug(detail.slug);
              }}
              type="button"
            >
              <span>{detail.shortTitle ?? detail.title}</span>
              <icons.ArrowRightIcon className="size-5 stroke-2" />
            </button>
          );
        })}
      </div>
    </>
  );
};

const Root = ({ children, className, product, ref, ...props }: ProductCardProps) => {
  const [selectedDetailSlug, setSelectedDetailSlug] = useState<string | null>(null);

  return (
    <ProductCardContext.Provider
      value={{
        ...product,
        selectedDetailSlug,
        setSelectedDetailSlug,
      }}
    >
      <Card className={className} ref={ref} {...props}>
        {children}
      </Card>
    </ProductCardContext.Provider>
  );
};

export const ProductCard = {
  Root,
  Name,
  Price,
  CartLink,
  Format,
  Rating,
  UpdatedAt,
  WhyRecommended,
  Carousel,
  Description,
  DosingAndEffects,
  Image,
  Instructions,
  Details,
};
